import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import { Text } from '../../common';
import maverickPortalhttp from '../../utils/webService/webService';
import maverickPortalApiEndPoint from '../../config/apiEndPoint';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import mixPanel from '../../utils/helpers/mixPanel';
import { validateWebLinkUrl } from '../../utils/helpers/urlValidator';
import InvalidProjectScreen from './InvalidProjectScreen';
import ErrorScreen1 from '../../assets/images/ErrorScreen1.png';

const OTP_LENGTH = 4;

const Page = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  background: #ffffff;
  display: flex;
  justify-content: center;
  align-items: stretch;
  padding-top: calc(24px + env(safe-area-inset-top, 0px));
  padding-right: calc(20px + env(safe-area-inset-right, 0px));
  padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  padding-left: calc(20px + env(safe-area-inset-left, 0px));
  box-sizing: border-box;
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-height: 740px) {
    padding-top: calc(12px + env(safe-area-inset-top, 0px));
    padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  }
`;

const Content = styled.div`
  width: min(390px, 100%);
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 100%;
  box-sizing: border-box;
`;

const Logo = styled.img`
  width: 168px;
  height: auto;
  display: block;
  margin: 12px auto 36px;

  @media (max-height: 740px) {
    width: 132px;
    margin: 4px auto 16px;
  }
`;

const Title = styled.h1`
  margin: 0 0 28px;
  font-size: 26px;
  line-height: 1.3;
  font-weight: 700;
  color: #333333;
  text-align: center;
`;

const FieldLabel = styled.label`
  display: block;
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 700;
  color: #333333;
  text-align: left;
`;

const MobileBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 52px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f3f3f3;
  box-sizing: border-box;
`;

const MobileValue = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #333333;
  word-break: break-all;
`;

const ChangeLink = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-size: 14px;
  font-weight: 700;
  color: #9ebe21;
  text-decoration: underline;
  cursor: pointer;
  flex-shrink: 0;
`;

const SmsHint = styled.p`
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.4;
  color: #9ebe21;
  text-align: left;
`;

const CodeSection = styled.div`
  margin-top: 28px;
`;

const OTPContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin: 10px 28px 0;
`;

const OTPInput = styled.input`
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  color: #333333;
  border: 1.5px solid
    ${props =>
      props.$hasError
        ? '#e53935'
        : props.$hasValue || props.$isActive
          ? '#9ebe21'
          : '#d8d8d8'};
  border-radius: 10px;
  background: #ffffff;
  outline: none;

  &:focus {
    border-color: ${props => (props.$hasError ? '#e53935' : '#9ebe21')};
  }
`;

const ErrorBanner = styled.div`
  margin: 12px 0 0;
  padding: 10px 14px;
  border-radius: 8px;
  background: #fdecea;
  color: #e53935;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
`;

const ResendRow = styled.div`
  margin-top: 14px;
  text-align: center;
  font-size: 13px;
  color: #777777;
`;

const ResendAction = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  font-size: 13px;
  font-weight: 700;
  color: ${props => (props.disabled ? '#999999' : '#9ebe21')};
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  text-decoration: ${props => (props.disabled ? 'none' : 'underline')};
`;

const VerifyButton = styled.button`
  display: block;
  width: 100%;
  flex-shrink: 0;
  margin-top: 12px;
  padding: 15px 16px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 700;
  color: #2f2f2f;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  background: ${props => (props.disabled ? '#d9d9d9' : '#9ebe21')};
  transition: opacity 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.94;
  }
`;

const SuccessText = styled(Text)`
  color: #9ebe21;
  font-size: 12px;
  margin-top: 10px;
  text-align: center;
  width: 100%;
  display: block;
`;

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.9);
  z-index: 1000;
`;

const Loader = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #9ebe21;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoaderMessage = styled.div`
  font-size: 16px;
  color: #333;
  font-weight: 500;
`;

function OTPScreen() {
  const navigate = useNavigate();
  mixPanel.init();
  const [searchParams] = useSearchParams();
  const getResponseSummary = useCallback((resp) => {
    if (!resp) return null;
    const source = resp.response ?? resp;
    if (!source || typeof source !== 'object') return null;
    const { config, data, status } = source;
    const summary = {};
    if (typeof config !== 'undefined') summary.config = config;
    if (typeof data !== 'undefined') summary.data = data;
    if (typeof status !== 'undefined') summary.status = status;
    return Object.keys(summary).length ? summary : null;
  }, []);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [userData, setUserData] = useState(null);
  const [activeOtpIndex, setActiveOtpIndex] = useState(0);
  const errorTimeoutRef = useRef(null);

  const showOtpError = useCallback((message, { clearBoxes = false } = {}) => {
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    if (clearBoxes) {
      setOtp(Array(OTP_LENGTH).fill(''));
      setActiveOtpIndex(0);
      setTimeout(() => document.getElementById('otp-input-0')?.focus(), 0);
    }
    setError(message);
    errorTimeoutRef.current = setTimeout(() => {
      setError('');
      errorTimeoutRef.current = null;
    }, 5000);
  }, []);

  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);
  const getDialCodeLabel = useCallback(() => {
    const raw = String(userData?.dial_code || userData?.country_code || '').trim();
    if (!raw) return '';
    if (raw.startsWith('+')) return raw;
    // country_code may be ISO like "IN" — prefer dial_code when present
    if (/^[A-Za-z]+$/.test(raw)) return '';
    return `+${raw.replace(/^\+/, '')}`;
  }, [userData]);

  const getDisplayMobile = useCallback(() => {
    const dial = getDialCodeLabel();
    const mobile = String(userData?.mobile || '').trim();
    if (!mobile) return dial || '';
    return dial ? `${dial} ${mobile}` : mobile;
  }, [getDialCodeLabel, userData]);

  const formatTimer = seconds => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const [canResend, setCanResend] = useState(false);
  const [timer, setTimer] = useState(30);
  const [projectId, setProjectId] = useState('');
  const [token1, setToken1] = useState('');
  const [projectSlug, setProjectSlug] = useState('');
  const [qRProject, setQRProject] = useState('');
  const [showInvalidScreen, setShowInvalidScreen] = useState(false);
  const [isValidatingWeblink, setIsValidatingWeblink] = useState(true);
  const [isGeneralized, setIsGeneralized] = useState(() => {
    const currentUrl = window.location.href;
    return !currentUrl.includes('&');
  });
 
  // Function to redirect to dashboard-resp
  const redirectToAuth = useCallback(() => {
    const currentUrl = window.location.href;
    const projectName = currentUrl.split('?')[1]?.split('=')[0] || '';
    const token5 = currentUrl.split('=')[1] || '';
    
    // Check if it's personalized or generalized
    const urlParams = new URLSearchParams(window.location.search);
    const projectIdParam = urlParams.get('Project_ID');
    const tokenParam = urlParams.get('Token');
    
    if (projectIdParam && tokenParam) {
      // Personalized format - redirect to dashboard-resp
      navigate(`/dashboard-resp?Project_ID=${projectIdParam}&Token=${tokenParam}`, { replace: true });
    } else if (projectName && token5) {
      // Generalized format - redirect to dashboard-resp
      navigate(`/dashboard-resp?${projectName}=${token5}`, { replace: true });
    } else {
      // Fallback - redirect to dashboard-resp
      navigate('/dashboard-resp', { replace: true });
    }
  }, [navigate]);

  // Clear previous webLink data when new link is opened
  useEffect(() => {
    // console.log('🧹 OTP Screen opened - clearing previous webLink data');
    
    // Clear all webLink-related sessionStorage data except signupData (needed for OTP)
    const webLinkKeys = [
      'form_answers',
      'text_input_answers', 
      'referral_code',
      'viewedMedia',
      'lastVisitedUrl',
      'lastVisitedToken',
      'lastVisitedProject',
      'token',
      'user_id',
      'project_IDper',
      'project_name',
      'gen_ID'
    ];
    
    webLinkKeys.forEach(key => {
      localStorage.removeItem(key);
    });
    
    // console.log('✅ Previous webLink data cleared from OTP screen');
  }, []);

  // Validate URL format, call weblinkValidation API, and check user data on component mount
  useEffect(() => {
    const validateWeblinkAndCheckData = async () => {
      // First validate URL format
      const urlValidation = validateWebLinkUrl();
      if (!urlValidation.isValid) {
        console.error('URL validation failed:', urlValidation.error);
        setShowInvalidScreen(true);
        setIsValidatingWeblink(false);
        return;
      }

      // Use validated identificationID from URL validation
      const validatedIdentificationID = urlValidation.identificationID;
      const validatedProjectIdentity = urlValidation.projectname || urlValidation.projectId;
      
      if (!validatedIdentificationID) {
        console.error('No URL parameter found after ? mark');
        setShowInvalidScreen(true);
        setIsValidatingWeblink(false);
        return;
      }

      // Call weblinkValidation API to check if link is valid
      try {
        setIsValidatingWeblink(true);
        const payload = {
          identificationID: validatedIdentificationID,
          projectIdentity: validatedProjectIdentity
        };

        const response = await maverickPortalhttp.makeRequest(
          maverickPortalApiEndPoint.weblinkValidation.url,
          maverickPortalApiEndPoint.weblinkValidation.methodType,
          payload
        );

        if (response && response.data.Status && response.data.Status.StatusCode === "-1") {
          if (response.data.Response) {
            // Check for project ended
            if (response.data.Response.projectEnd === 0) {
              setShowInvalidScreen(true);
              setIsValidatingWeblink(false);
              return;
            }
            
            // Check for invalid project link
            if (response.data.Response.isAvaliableProjectFlag === 0) {
              setShowInvalidScreen(true);
              setIsValidatingWeblink(false);
              return;
            }
          }
        }

        // If we reach here, the link is valid according to API
        setIsValidatingWeblink(false);

        // Now check for user data
        const storedData = localStorage.getItem('signupData');
        if (storedData) {
          try {
            const parsedData = JSON.parse(storedData);
            // Check if required data is present
            if (parsedData && parsedData.mobile && (parsedData.dial_code || parsedData.country_code)) {
              setUserData(parsedData);
            } else {
              // Valid link but missing required data - redirect to authentication
              redirectToAuth();
            }
          } catch (error) {
            console.error('Error parsing signupData:', error);
            // Valid link but invalid data - redirect to authentication
            redirectToAuth();
          }
        } else {
          // Valid link but no user data - redirect to authentication
          redirectToAuth();
        }
      } catch (error) {
        console.error('Error validating weblink:', error);
        // On API error, show invalid screen to be safe
        setShowInvalidScreen(true);
        setIsValidatingWeblink(false);
      }
    };

    validateWeblinkAndCheckData();
  }, [redirectToAuth]);

  // Clear signup data when component unmounts (e.g., when navigating back)
  useEffect(() => {
    // Handle browser back button and page unload
    const handleBeforeUnload = () => {
      localStorage.removeItem('signupData');
    };

    // Add event listener for page unload
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Clear signup data when navigating away from OTP screen.
      // Keep pendingCreateAccount so Create Account can show the verified number.
      localStorage.removeItem('signupData');
      // Remove event listener
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
 
  // Check if all OTP inputs are filled
  const isOTPComplete = otp.every(digit => digit !== '');
 
  useEffect(() => {
    // Don't process if invalid screen is showing
    if (showInvalidScreen) {
      return;
    }

    const projectId = searchParams.get('Project_ID');
    const token1 = searchParams.get('Token');
    const projectSlug = searchParams.get('projectSlug');
    const qRProject = searchParams.get('qRProject');

    setProjectId(projectId);
    setToken1(token1);
    setProjectSlug(projectSlug);
    setQRProject(qRProject);
  }, [searchParams, showInvalidScreen]);
 
  // Timer effect for resend cooldown
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  //User logged in check
  useEffect(() => {
    const project_name = localStorage.getItem('project_name');
    const gen_ID = localStorage.getItem('gen_ID');
    const isAuthenticated = checkAuth(); // Replace with your logic

    if (isAuthenticated) {
      // Redirect away from login page if already logged in
      if(isGeneralized){
      navigate(`/dashboard-resp?${project_name}=${gen_ID}`, { replace: true });
      }else{
      navigate(`/dashboard-resp?${project_name}&Token=${gen_ID}`, { replace: true });
      }
    }
  }, []);
  
  function checkAuth() {
    const token = localStorage.getItem('token');
    return !!token;
  }
 
  const handleOTPChange = (index, value) => {
    const digits = value.replace(/[^0-9]/g, '');
    if (error) {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
      setError('');
    }

    // Support paste of full OTP into any box
    if (digits.length > 1) {
      const next = Array(OTP_LENGTH).fill('');
      digits.slice(0, OTP_LENGTH).split('').forEach((d, i) => {
        next[i] = d;
      });
      setOtp(next);
      const focusAt = Math.min(digits.length, OTP_LENGTH - 1);
      setActiveOtpIndex(focusAt);
      document.getElementById(`otp-input-${focusAt}`)?.focus();
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = digits.slice(-1);
    setOtp(newOtp);

    if (digits && index < OTP_LENGTH - 1) {
      setActiveOtpIndex(index + 1);
      document.getElementById(`otp-input-${index + 1}`)?.focus();
    } else {
      setActiveOtpIndex(index);
    }
  };
 
  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      setActiveOtpIndex(index - 1);
      document.getElementById(`otp-input-${index - 1}`)?.focus();
    }
  };
 
  const verifyOTP = () => {
    const otpValue = otp.join('');
    if (otpValue.length !== OTP_LENGTH) {
      setError('Please enter complete OTP');
      return;
    }
 
    if (!userData) {
      setError('User data not found. Please try again.');
      return;
    }

    const currentUrl = window.location.href;
    const projectName = currentUrl.split('?')[1]?.split('=')[0] || '';
    const token5 = currentUrl.split('=')[1] || '';
    const personalizedToken = currentUrl.split('=')[1] || '';
    const dialCode = userData?.dial_code;
    const phoneNumber = userData?.phone || (dialCode ? `${dialCode}${userData.mobile}` : userData.mobile);
    const data = {
      mobile: userData.mobile,
      country_code: userData.country_code,
      phone: phoneNumber,
      code: otpValue,
      Type: "Login",
      isGeneralized: isGeneralized ? 1 : 0,
      genID: isGeneralized ? token5 : '' , // 
      perID: !isGeneralized ? personalizedToken : ''
    };

 
    maverickPortalhttp
      .makeRequest(
        maverickPortalApiEndPoint.twilioCheckWebLinkOTP2.url,
        maverickPortalApiEndPoint.twilioCheckWebLinkOTP2.methodType,
        data
      )
      .then((response) => {
        if (response.data?.Status?.StatusCode == 1) {
          localStorage.setItem('token', response.data.Response.token_data.access_token);
          localStorage.setItem('user_id', response.data.Response.user_id);
          if (response.data.Response.user_name) {
            localStorage.setItem('user_name', response.data.Response.user_name);
          } else {
            localStorage.removeItem('user_name');
          }
          localStorage.setItem('project_IDper', response.data.Response.project_ID);
          localStorage.setItem('project_name', response.data.Response.projectName);
          localStorage.setItem('gen_ID', response.data.Response.genID);
          
          // Get token from URL params to pass during navigation
          const urlParams = new URLSearchParams(window.location.search);
          let tokenFromParams = urlParams.get('Token');
          
          // If Token parameter doesn't exist (generalized case), get token from the project name parameter
          if (!tokenFromParams) {
            const currentUrl = window.location.href;
            const urlParts = currentUrl.split('?')[1];
            if (urlParts) {
              const paramParts = urlParts.split('=');
              if (paramParts.length > 1) {
                tokenFromParams = paramParts[1];
              }
            }
          }
          
          if(!isGeneralized){
           navigate(`/dashboard-resp?${response.data.Response.projectName}&Token=${tokenFromParams}`, { replace: true });
          }else{
            navigate(`/dashboard-resp?${response.data.Response.projectName}=${tokenFromParams}`, { replace: true });
          }
          setSuccessMessage('OTP verified successfully!');
        } else if (response.data?.Status?.StatusCode == 2) {
          // No account for this number — send user to Create Account with verified phone.
          localStorage.setItem(
            'pendingCreateAccount',
            JSON.stringify({
              mobile: userData?.mobile || '',
              dial_code: userData?.dial_code || '',
              country_code: userData?.country_code || '',
              phone: phoneNumber,
            })
          );
          const currentUrl = window.location.href;
          const projectName = currentUrl.split('?')[1]?.split('=')[0] || '';
          const token5 = currentUrl.split('=')[1] || '';
          if (!projectId) {
            navigate(`/sign-up?${projectName}=${token5}`, { replace: true });
          } else {
            navigate(`/sign-up?Project_ID=${projectId}&Token=${token1}`, { replace: true });
          }
        } else if(response.data?.Status?.StatusCode == -1) {
          mixPanel.track('OTP Verification Failed', {
            error: response?.data?.Status?.StatusDesc || 'OTP verification failed',
            mobile: userData?.mobile || 'unknown',
            countryCode: userData?.country_code || 'unknown',
            otpLength: otpValue.length,
            isGeneralized: isGeneralized,
            errorCode: 443,
            errorDetails: getResponseSummary(response),
            endpoint: maverickPortalApiEndPoint.twilioCheckWebLinkOTP.url,
            timestamp: new Date().toISOString(),
            request:data,
            projectId: projectId,
            token: token1
          });
          showOtpError("This code didn't match or has expired", { clearBoxes: true });
        }
      })
      .catch((error) => {
        console.error('Verification error:', error);
        const errorReason = error.response?.data?.Status?.StatusDesc || error.response?.data?.message || error.message || 'Unknown error';
        mixPanel.track('OTP Verification Failed', {
          error: `error from otp verify: ${errorReason}`,
          mobile: userData?.mobile || 'unknown',
          countryCode: userData?.country_code || 'unknown',
          otpLength: otpValue.length,
          isGeneralized: isGeneralized,
          errorCode: 443,
          errorDetails: getResponseSummary(error),
          endpoint: maverickPortalApiEndPoint.twilioCheckWebLinkOTP.url,
          timestamp: new Date().toISOString(),
          request: data,
          projectId: projectId,
          token: token1
        });
        showOtpError("This code didn't match or has expired", { clearBoxes: true });
      });
  };
 
  const handleResendOTP = () => {
    if (!canResend) return;
 
    setCanResend(false);
    setTimer(30);
    setError('');
    setSuccessMessage('');
 
    const dialCode = userData?.dial_code;
    const phoneNumber = userData?.phone || (dialCode ? `${dialCode}${userData.mobile}` : userData.mobile);
    const data = {
      phone: phoneNumber,
      Type: "Login",
      mobile: userData.mobile
    };
 
 
    maverickPortalhttp
      .makeRequest(
        maverickPortalApiEndPoint.twilioSendOTP2.url,
        maverickPortalApiEndPoint.twilioSendOTP2.methodType,
        data
      )
      .then((response) => {
        if (response.data?.Status?.StatusCode === 1) {
         
          setSuccessMessage('OTP resent successfully!');
          setOtp(Array(OTP_LENGTH).fill(''));
          setActiveOtpIndex(0);
          document.getElementById('otp-input-0')?.focus();
        } else if(response.data?.Status?.StatusCode == -1) {
          setTimeout(()=>{
             setError(response.data?.Status?.StatusDesc || 'Failed to resend OTP');
          },100)
          setTimeout(()=>{
            setError("");
          },5000)
          setCanResend(true);
          setTimer(0);
        }
      })
      .catch((error) => {
        console.error('Resend OTP error:', error);
        setError('Failed to resend OTP. Please try again.');
        setCanResend(true);
        setTimer(0);
      });
  };
 
  const handleEditClick = () => {
    // Navigate back to the previous screen
    navigate(-1);
  };
 
  // Show loading while validating weblink
  if (isValidatingWeblink) {
    return (
      <LoaderWrapper>
        <Loader />
        <LoaderMessage>Loading...</LoaderMessage>
      </LoaderWrapper>
    );
  }

  // Show invalid screen if URL is invalid or API validation failed
  if (showInvalidScreen) {
    return <InvalidProjectScreen backgroundImageUrl={ErrorScreen1} />;
  }

  // If user data is not found or incomplete, redirect to dashboard-resp (handled in useEffect)
  // This check is a fallback in case redirect didn't happen
  if (!userData || !userData.mobile || (!userData.dial_code && !userData.country_code)) {
    return (
      <LoaderWrapper>
        <Loader />
        <LoaderMessage>Redirecting...</LoaderMessage>
      </LoaderWrapper>
    );
  }
 
  return (
    <Page>
      <Content>
        <Logo src={`${process.env.PUBLIC_URL}/logo.png`} alt="explorastory" />
        <Title>Enter the code we sent you</Title>

        <FieldLabel>Mobile number</FieldLabel>
        <MobileBox>
          <MobileValue>{getDisplayMobile()}</MobileValue>
          <ChangeLink type="button" onClick={handleEditClick}>
            Change
          </ChangeLink>
        </MobileBox>
        <SmsHint>OTP sent by SMS to your number</SmsHint>

        <CodeSection>
          <FieldLabel htmlFor="otp-input-0">{OTP_LENGTH}-digit code</FieldLabel>
          <OTPContainer>
            {otp.map((digit, index) => (
              <OTPInput
                key={index}
                id={`otp-input-${index}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength={index === 0 ? OTP_LENGTH : 1}
                value={digit}
                $hasValue={Boolean(digit)}
                $isActive={activeOtpIndex === index}
                $hasError={Boolean(error)}
                onFocus={() => setActiveOtpIndex(index)}
                onChange={e => handleOTPChange(index, e.target.value)}
                onKeyDown={e => handleOTPKeyDown(index, e)}
              />
            ))}
          </OTPContainer>

          {error && <ErrorBanner>{error}</ErrorBanner>}

          <ResendRow>
            {canResend ? (
              <>
                Didn&apos;t get it?{' '}
                <ResendAction type="button" onClick={handleResendOTP}>
                  Resend
                </ResendAction>
              </>
            ) : (
              <>Didn&apos;t get it? Resend in {formatTimer(timer)}</>
            )}
          </ResendRow>

          <VerifyButton type="button" onClick={verifyOTP} disabled={!isOTPComplete || Boolean(error)}>
            Verify
          </VerifyButton>

          {successMessage && !error && <SuccessText>{successMessage}</SuccessText>}
        </CodeSection>
      </Content>
    </Page>
  );
}
 
export default OTPScreen;